import { useQuery } from "@tanstack/react-query";
import { Check, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { getPmlNodos, type NodoTipo, type PmlNodo } from "@/lib/api";

type SelectedNode = { tipo: NodoTipo; clave: string };

type Props = {
  open: boolean;
  initialSelection: SelectedNode[];
  onClose: () => void;
  onApply: (selection: SelectedNode[]) => void;
};

const TIPO_OPTIONS: { value: NodoTipo | "ALL"; label: string }[] = [
  { value: "ALL", label: "Todos" },
  { value: "PML", label: "PML" },
  { value: "PND", label: "PND" },
];

function nodeKey(n: { tipo: NodoTipo; clave: string }): string {
  return `${n.tipo}:${n.clave}`;
}

export function NodeSelectorModal({
  open,
  initialSelection,
  onClose,
  onApply,
}: Props) {
  const [tipo, setTipo] = useState<NodoTipo | "ALL">("ALL");
  const [sistema, setSistema] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [debounced, setDebounced] = useState<string>("");
  const [selected, setSelected] = useState<Map<string, SelectedNode>>(new Map());

  useEffect(() => {
    if (open) {
      const m = new Map<string, SelectedNode>();
      initialSelection.forEach((n) => m.set(nodeKey(n), n));
      setSelected(m);
    }
  }, [open, initialSelection]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const nodosQ = useQuery({
    queryKey: ["pml-nodos", tipo, sistema, debounced],
    queryFn: () =>
      getPmlNodos({
        tipo,
        sistema: sistema || undefined,
        search: debounced || undefined,
        limit: 5000,
      }),
    enabled: open,
  });

  const sistemas = useMemo(() => {
    const set = new Set<string>();
    nodosQ.data?.items.forEach((n) => n.sistema && set.add(n.sistema));
    return Array.from(set).sort();
  }, [nodosQ.data]);

  const toggle = (n: PmlNodo) => {
    const k = nodeKey(n);
    const next = new Map(selected);
    if (next.has(k)) next.delete(k);
    else next.set(k, { tipo: n.tipo, clave: n.clave });
    setSelected(next);
  };

  const toggleAll = () => {
    if (!nodosQ.data) return;
    const next = new Map(selected);
    const visibles = nodosQ.data.items;
    const allSelected = visibles.every((n) => next.has(nodeKey(n)));
    if (allSelected) {
      visibles.forEach((n) => next.delete(nodeKey(n)));
    } else {
      visibles.forEach((n) => next.set(nodeKey(n), { tipo: n.tipo, clave: n.clave }));
    }
    setSelected(next);
  };

  if (!open) return null;

  const selectedArr = Array.from(selected.values());

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="font-semibold">Seleccionar nodos</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Tipo</span>
            <select
              className="mt-1 border rounded px-2 py-1.5 w-full"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as NodoTipo | "ALL")}
            >
              {TIPO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Sistema</span>
            <select
              className="mt-1 border rounded px-2 py-1.5 w-full"
              value={sistema}
              onChange={(e) => setSistema(e.target.value)}
            >
              <option value="">Todos</option>
              {sistemas.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Buscar clave</span>
            <div className="mt-1 relative">
              <Search className="w-4 h-4 absolute left-2 top-2 text-slate-400" />
              <input
                type="text"
                className="border rounded pl-8 pr-2 py-1.5 w-full"
                placeholder="ej. 02CIR-115"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </label>
        </div>

        <div className="flex-1 overflow-auto">
          {nodosQ.isLoading && (
            <div className="px-5 py-8 text-center text-slate-500">Cargando…</div>
          )}
          {nodosQ.isError && (
            <div className="px-5 py-8 text-center text-red-600">
              Error: {(nodosQ.error as Error).message}
            </div>
          )}
          {nodosQ.data && (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-left sticky top-0">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <button
                      type="button"
                      onClick={toggleAll}
                      className="border rounded px-1 text-xs hover:bg-white"
                      title="Seleccionar/deseleccionar todos los visibles"
                    >
                      ◧
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">Tipo</th>
                  <th className="px-3 py-2 font-medium">Clave</th>
                  <th className="px-3 py-2 font-medium">Nombre</th>
                  <th className="px-3 py-2 font-medium">Zona</th>
                  <th className="px-3 py-2 font-medium">Sistema</th>
                </tr>
              </thead>
              <tbody>
                {nodosQ.data.items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                      Sin resultados.
                    </td>
                  </tr>
                )}
                {nodosQ.data.items.map((n) => {
                  const k = nodeKey(n);
                  const isSel = selected.has(k);
                  return (
                    <tr
                      key={k}
                      className={`border-t cursor-pointer ${
                        isSel ? "bg-blue-50" : "hover:bg-slate-50"
                      }`}
                      onClick={() => toggle(n)}
                    >
                      <td className="px-3 py-1.5">
                        <div
                          className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isSel ? "bg-blue-600 border-blue-600" : "bg-white border-slate-300"
                          }`}
                        >
                          {isSel && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <span
                          className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                            n.tipo === "PML"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {n.tipo}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 font-mono text-xs">{n.clave}</td>
                      <td className="px-3 py-1.5 text-slate-700">{n.nombre ?? "—"}</td>
                      <td className="px-3 py-1.5 text-slate-600">{n.zona_carga ?? "—"}</td>
                      <td className="px-3 py-1.5 text-slate-600">{n.sistema}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-between gap-3">
          <div className="text-sm text-slate-600">
            {selectedArr.length} seleccionado{selectedArr.length === 1 ? "" : "s"}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelected(new Map())}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Limpiar
            </button>
            <button
              type="button"
              onClick={onClose}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => {
                onApply(selectedArr);
                onClose();
              }}
              className="bg-blue-600 text-white rounded px-4 py-1.5 text-sm hover:bg-blue-700"
            >
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
